<template lang="html">
  <div
    v-if="tabItems.length"
    id="product-content-tabs"
    class="product-content-tabs scroll-mt-[calc(var(--ui-header-height)+1rem)]"
  >
    <UTabs
      v-model="activeTab"
      :items="tabItems"
      :content="false"
      variant="link"
      color="tertiary"
    />

    <div class="mt-4">
      <p
        v-if="activeTab === 'description' && description"
        class="text-sm whitespace-pre-wrap"
      >
        {{ description }}
      </p>

      <AttributesSpecsList
        v-else-if="activeTab === 'specs'"
        :value="props.value"
      />

      <p
        v-else-if="activeTab === 'promo' && promoTerms"
        class="text-sm whitespace-pre-wrap text-muted"
      >
        {{ promoTerms }}
      </p>

      <div
        v-else-if="activeTab === 'documents'"
        class="grid max-w-2xl gap-3 sm:grid-cols-2"
      >
        <a
          v-for="document in documentCards"
          :key="document.id"
          :href="document.href"
          target="_blank"
          rel="noopener noreferrer"
          class="group flex min-w-0 items-center gap-3 rounded-lg border border-default bg-default p-3.5 transition-colors hover:border-primary hover:bg-elevated"
        >
          <div
            class="flex size-11 shrink-0 items-center justify-center rounded-md"
            :class="document.badgeClass"
          >
            <UIcon
              :name="document.icon"
              class="size-5"
            />
          </div>

          <div class="min-w-0 flex-1">
            <div class="truncate text-sm font-medium text-highlighted">
              {{ document.name }}
            </div>
            <div class="mt-0.5 text-xs text-muted">
              {{ document.ext }} · Скачать
            </div>
          </div>

          <UIcon
            name="i-lucide-download"
            class="size-4 shrink-0 text-muted transition-colors group-hover:text-primary"
          />
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TabsItem } from '@nuxt/ui'
import type { ProductDto } from '@repo/api/web/models'

import AttributesSpecsList from './AttributesSpecsList.vue'

const props = defineProps<{
  value: ProductDto
}>()

const { support } = useResources()

const activeTab = ref('description')

const description = computed(
  () => props.value.wellKnownAttributes?.Description?.trim() || '',
)

const promoTerms = computed(() => props.value.promoTerms?.trim() || '')

const wellKnownSystemNames = computed(() =>
  Object.keys(props.value.wellKnownAttributes || {}),
)

const variantKeyAttributeIds = computed(() =>
  Object.keys(props.value.variants?.keyWithValues || {}),
)

const hasSpecs = computed(() => {
  if (!props.value.groups?.length) {
    return false
  }

  return props.value.groups.some((group) => {
    const attrs = group.attributes || []

    return attrs.some(
      attr =>
        attr.metadata.isScalar
        && !wellKnownSystemNames.value.includes(
          attr.metadata.systemName || attr.metadata.displayName || '',
        )
        && !variantKeyAttributeIds.value.includes(String(attr.metadata.id)),
    )
  })
})

const documents = computed(() => props.value.documents || [])

const documentCards = computed(() =>
  documents.value.map((document) => {
    const href = getDocumentUrl(document.id)
    const name = document.displayName?.trim() || document.id
    const ext = fileExtension(href, name)
    const isPdf = ext === 'PDF'

    return {
      id: document.id,
      href,
      name,
      ext,
      icon: isPdf ? 'i-lucide-file-text' : 'i-lucide-file',
      badgeClass: isPdf
        ? 'bg-error/10 text-error'
        : 'bg-elevated text-muted',
    }
  }),
)

const tabItems = computed<TabsItem[]>(() => {
  const items: TabsItem[] = []

  if (description.value) {
    items.push({ label: 'Описание', value: 'description' })
  }

  if (hasSpecs.value) {
    items.push({ label: 'Характеристики', value: 'specs' })
  }

  if (promoTerms.value) {
    items.push({ label: 'Акция', value: 'promo' })
  }

  if (documents.value.length) {
    items.push({ label: 'Документы', value: 'documents' })
  }

  return items
})

watch(
  tabItems,
  (items) => {
    if (!items.length) {
      return
    }

    if (!items.some(item => item.value === activeTab.value)) {
      activeTab.value = String(items[0]?.value)
    }
  },
  { immediate: true },
)

function openTab(tab: string) {
  if (!tabItems.value.some(item => item.value === tab)) {
    return false
  }

  activeTab.value = tab
  return true
}

function getDocumentUrl(resourceId: string) {
  return support.getResourceUrl(resourceId)
}

function fileExtension(href: string, name: string) {
  const fromUrl = href.split('?')[0]?.split('.').pop()
  const fromName = name.split('.').pop()
  const raw = (fromUrl && fromUrl.length <= 5 ? fromUrl : fromName) || 'file'

  return raw.toUpperCase()
}

defineExpose({ openTab })
</script>
